import React, { useState } from "react";
import { PlayCircle, Clock, HelpCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { RevealStagger, StaggerItem, Reveal } from "@/components/Reveal";
import { EDUCATION } from "@/data/content";

const THUMBS = [
  "https://images.pexels.com/photos/17483871/pexels-photo-17483871.png",
  "https://images.pexels.com/photos/4506111/pexels-photo-4506111.jpeg",
  "https://images.pexels.com/photos/26729558/pexels-photo-26729558.jpeg",
  "https://images.pexels.com/photos/4506109/pexels-photo-4506109.jpeg",
  "https://images.pexels.com/photos/7659564/pexels-photo-7659564.jpeg",
];

const PatientEducation = () => {
  const [active, setActive] = useState(null);
  const video = active !== null ? EDUCATION[active] : null;

  const handleClick = (v, i) => {
    if (v.youtube) {
      setActive(i);
    } else if (v.href) {
      const el = document.querySelector(v.href);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section id="education" data-testid="patient-education" className="relative bg-slate-50 py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Reveal className="max-w-2xl mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand">06 — Patient Education</p>
          <h2 className="font-heading font-extrabold text-[#0b2c4d] text-4xl md:text-5xl tracking-tight mt-3 leading-tight">
            Understand your spine, at your pace
          </h2>
          <p className="mt-5 text-lg text-slate-500">
            Clear, jargon-free explainers to help you make informed decisions. No autoplay — you are in control.
          </p>
        </Reveal>

        <RevealStagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {EDUCATION.map((v, i) => (
            <StaggerItem key={v.title}>
              <button
                onClick={() => handleClick(v, i)}
                data-testid={`education-card-${i}`}
                className="group text-left w-full glass-card rounded-2xl overflow-hidden hover:-translate-y-1.5 hover:shadow-lg transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={THUMBS[i % THUMBS.length]}
                    alt={v.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0b2c4d]/75 via-[#0b2c4d]/20 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    {v.youtube ? (
                      <PlayCircle className="w-14 h-14 text-white group-hover:scale-110 transition-transform" />
                    ) : (
                      <HelpCircle className="w-14 h-14 text-white group-hover:scale-110 transition-transform" />
                    )}
                  </div>
                  <span className="absolute top-4 left-4 text-[10px] uppercase tracking-wider bg-brand text-white font-bold px-2.5 py-1 rounded-full">
                    {v.tag}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-heading font-extrabold text-[#0b2c4d] text-lg tracking-tight leading-snug">
                    {v.title}
                  </h3>
                  <div className="mt-3 flex items-center gap-1.5 text-slate-400 text-xs">
                    <Clock className="w-3.5 h-3.5" /> {v.minutes}
                  </div>
                </div>
              </button>
            </StaggerItem>
          ))}
        </RevealStagger>
      </div>

      {/* Video player */}
      <Dialog open={active !== null} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="video-modal"
          className="sm:max-w-[820px] bg-white border-slate-200 text-[#0b2c4d] p-0 overflow-hidden"
        >
          {video && video.youtube && (
            <>
              <div className="relative w-full aspect-video bg-black">
                <iframe
                  data-testid="video-iframe"
                  className="absolute inset-0 w-full h-full"
                  src={`https://www.youtube-nocookie.com/embed/${video.youtube}?rel=0&modestbranding=1`}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="px-6 py-5">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl font-extrabold tracking-tight text-[#0b2c4d] text-left">
                    {video.title}
                  </DialogTitle>
                  <DialogDescription className="sr-only">
                    Educational patient video: {video.title}
                  </DialogDescription>
                </DialogHeader>
                <p className="mt-2 text-xs text-slate-400">
                  Educational video for general awareness — not a substitute for professional medical advice.
                </p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default PatientEducation;
